import React, { useEffect, useRef, useState } from 'react';

import ContentfulImage from '../Common/ContentfulImage';
import { ArrowLink } from '../Common/Links';
import PageSection from '../PageSections/PageSection';
import * as styles from './Accordion.module.scss';

const ANIMATION_SPEED = 4000;
const ANIMATION_KEYFRAMES = [
  {
    transform: 'scaleY(0)',
  },
  { transform: 'none' },
];
const ANIMATION_SETTINGS = { fill: 'forwards', easing: 'linear', duration: ANIMATION_SPEED };

const Accordion = React.forwardRef((props, ref) => {
  const section = props.section;
  const [activeTab, setActiveTab] = useState(0);
  const [canAnimate, setCanAnimate] = useState(true);
  const container = useRef();
  const progressBar = useRef();
  const format = (string) => string.toLowerCase().replaceAll(' ', '-');

  // Resume animation upon mouseleave
  const resumeAnimation = () => setCanAnimate(true);
  const resetProgress = () => {
    if (progressBar.current) {
      progressBar.current.animate(ANIMATION_KEYFRAMES, { ...ANIMATION_SETTINGS, duration: 1 });
      progressBar.current.style.transform = 'none';
    }
  };

  useEffect(() => {
    const target = container.current;
    if (!target) return;
    if (target) {
      target.addEventListener('mouseleave', resumeAnimation);
      return () => target.removeEventListener('mouseleave', resumeAnimation);
    }
  }, [container]);

  // play progress animation
  useEffect(() => {
    if (canAnimate && progressBar.current) {
      progressBar.current.animate(ANIMATION_KEYFRAMES, ANIMATION_SETTINGS);

      const timeout = setTimeout(() => {
        if (activeTab + 1 > 2) {
          setActiveTab(0);
        } else {
          setActiveTab((value) => value + 1);
        }
      }, [ANIMATION_SPEED]);

      return () => clearTimeout(timeout);
    } else {
      resetProgress();
    }
  }, [canAnimate, activeTab]);

  // Pause timeout upon item click
  const handleClick = (e, i) => {
    if (e.target.nodeName !== 'A') e.preventDefault();
    setActiveTab(i);
    setCanAnimate(false);
    resetProgress();
  };

  return (
    <PageSection data-analytics-section="homepage-accordion" wide slim className={styles.section}>
      <h2 ref={ref}>{section.title}</h2>
      <div className={styles.grid} ref={container}>
        <div className={styles.imageWrapper}>
          {section.content.map((item, i) => (
            <ContentfulImage
              data-analytics-component="homepage-accordion-image"
              data-analytics-id={`${format(item.title)}-image`}
              imageFragment={item.mainImage}
              lazy={true}
              style={{ opacity: i === activeTab ? 1 : 0 }}
              className={styles.image}
              key={i}
            />
          ))}
        </div>
        <div className={styles.copyWrapper}>
          <div ref={progressBar} className={styles.progressBar} />
          {section.content.map((item, i) => (
            <details
              data-analytics-component="homepage-accordion-tab"
              data-analytics-id={`${format(item.title)}-tab`}
              className={styles.copy}
              open={i === activeTab}
              onClick={(e) => handleClick(e, i)}
              key={i}
            >
              <summary>{item.title}</summary>
              <div className={styles.content}>
                <p>{item.subtitle}</p>
                <ArrowLink url={item.ctaUrl} label={item.ctaLabel} hoverAnimation inlineArrow />
              </div>
            </details>
          ))}
        </div>
      </div>
    </PageSection>
  );
});

export default Accordion;
